import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { playClick, playSuccess, playHomeRun } from '@/utils/sounds';
import { AvatarCanvasSmall } from '@/components/AvatarCanvas';
import OnlineGame from '@/components/OnlineGame';
import OnlineEndGame from '@/components/OnlineEndGame';
import {
  RetroBackground,
  RetroButton,
  RetroPageTitle,
  bandedGradient,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slot row inside a team panel
function SlotRow({ slot }) {
  const filled = !!slot.user_id;
  return (
    <div
      className="flex items-center gap-2 px-3 py-2"
      style={{
        background: filled ? '#FFFFFF' : 'transparent',
        border: filled ? `2px solid ${PALETTE.ink}` : `2px dashed ${PALETTE.ink}66`,
        borderRadius: DESIGN_TOKENS.borderRadius.md,
        minHeight: '44px',
      }}
      data-testid={`lobby-slot-${slot.slot_index}`}
    >
      {filled ? (
        <>
          {slot.avatar_config && Object.keys(slot.avatar_config).length > 0 && (
            <span className="inline-flex">
              <AvatarCanvasSmall config={slot.avatar_config} size={26} />
            </span>
          )}
          <span style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontWeight: 700,
            fontSize: '13px',
            color: PALETTE.ink,
            flex: 1,
          }}>
            {slot.username}
          </span>
          <span
            className="rounded-full"
            style={{
              width: '10px',
              height: '10px',
              background: slot.is_connected ? PALETTE.grass : '#C0392B',
              border: `2px solid ${PALETTE.ink}`,
            }}
            title={slot.is_connected ? 'Connected' : 'Disconnected'}
          />
        </>
      ) : (
        <span
          className="animate-pulse"
          style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '12px',
            color: `${PALETTE.ink}77`,
          }}
        >
          Waiting for player...
        </span>
      )}
    </div>
  );
}

// Team panel with framed header band
function TeamPanel({ teamId, team, isMyTeam }) {
  const voteCount = Object.keys(team?.name_votes || {}).length;
  const filledCount = (team?.slots || []).filter((s) => s.user_id).length;
  return (
    <div
      style={{
        background: PALETTE.cream,
        border: `4px solid ${PALETTE.ink}`,
        borderRadius: DESIGN_TOKENS.borderRadius.lg,
        boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
        overflow: 'hidden',
      }}
      data-testid={`lobby-team-${teamId}`}
    >
      <div
        className="text-center py-2 px-1"
        style={{
          fontFamily: DESIGN_TOKENS.fonts.display,
          fontSize: '10px',
          color: PALETTE.cream,
          background: isMyTeam ? PALETTE.grass : PALETTE.dirt,
          borderBottom: `3px solid ${PALETTE.ink}`,
          letterSpacing: '1px',
        }}
      >
        {team?.name ? team.name.toUpperCase() : `TEAM ${teamId}`}
        {isMyTeam ? ' ★' : ''}
      </div>
      <div className="p-2 space-y-2">
        {(team?.slots || []).map((slot) => (
          <SlotRow key={slot.slot_index} slot={slot} />
        ))}
        {voteCount > 0 && !team?.name && (
          <p className="text-center" style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '11px',
            color: PALETTE.dirt,
          }}>
            {voteCount}/{filledCount} voted
          </p>
        )}
      </div>
    </div>
  );
}

export default function OnlineLobby() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { getAuthHeader, token, user } = useAuth();

  const [match, setMatch] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [gameMsg, setGameMsg] = useState(null);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const msgSeqRef = useRef(0);

  const wsRef = useRef(null);
  const mountedRef = useRef(true);
  const matchStateRef = useRef(null);
  const myTeamRef = useRef(null);
  const readyCalledRef = useRef(false);
  const finalizeCalledRef = useRef(false);
  const startCalledRef = useRef(false);
  const countdownAnnouncedRef = useRef(false);

  // Reset per-match state when navigating to a new match (e.g. rematch)
  useEffect(() => {
    setMatch(null);
    setCountdown(null);
    setGameMsg(null);
    setConfirmAbandon(false);
    matchStateRef.current = null;
    readyCalledRef.current = false;
    finalizeCalledRef.current = false;
    startCalledRef.current = false;
    countdownAnnouncedRef.current = false;
    msgSeqRef.current = 0;
  }, [matchId]);

  const fetchMatch = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}`, {
        headers: getAuthHeader(),
      });
      if (res.status === 404 || res.status === 403) {
        toast.error('Match no longer available');
        navigate('/online');
        return;
      }
      const data = await res.json();
      if (!mountedRef.current) return;
      matchStateRef.current = data.state;
      myTeamRef.current = data.my_team;
      setMatch(data);
    } catch (e) {
      // keep polling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  // Load playlists for announcement
  useEffect(() => {
    fetch(`${API_URL}/api/apple-music/playlists`)
      .then((r) => r.json())
      .then(setPlaylists)
      .catch(() => {});
  }, []);

  // Poll match state (source of truth)
  useEffect(() => {
    mountedRef.current = true;
    fetchMatch();
    const interval = setInterval(fetchMatch, 2000);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchMatch]);

  // WebSocket for live updates
  useEffect(() => {
    if (!token || !matchId) return undefined;
    let ws;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      const wsUrl = `${API_URL.replace(/^http/, 'ws')}/api/ws/online/${matchId}?token=${token}`;
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (mountedRef.current) setWsConnected(true);
      };
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          // Live match notices
          if (msg.type === 'player_left' && msg.payload?.abandoned) {
            const who = msg.payload.username || 'A player';
            if (msg.payload.team === myTeamRef.current) {
              toast.warning(`${who} abandoned — your team plays short-handed!`, { duration: 6000 });
            } else {
              toast.info(`${who} abandoned the other team`, { duration: 5000 });
            }
          }
          if (msg.type === 'player_joined' && msg.payload?.backfilled) {
            toast.success(`${msg.payload.username || 'A player'} joined the lobby!`);
          }
          if (msg.type === 'error' && msg.payload?.match_cancelled) {
            toast.warning('Match cancelled — a player abandoned before the game started', { duration: 6000 });
          }
          const lobbyTypes = [
            'player_joined', 'player_left', 'vote_update',
            'team_name_set', 'countdown_start', 'game_start', 'state_sync',
            'rematch_update', 'error',
          ];
          const gameTypes = [
            'pitcher_turn', 'song_selected', 'batter_turn',
            'at_bat_result', 'half_inning_end', 'game_end',
          ];
          if (lobbyTypes.includes(msg.type)) {
            fetchMatch();
          }
          if (gameTypes.includes(msg.type)) {
            msgSeqRef.current += 1;
            setGameMsg({ type: msg.type, payload: msg.payload, seq: msgSeqRef.current });
          }
        } catch (e) {
          // ignore malformed
        }
      };
      ws.onclose = () => {
        if (mountedRef.current) setWsConnected(false);
        if (!closed && mountedRef.current && matchStateRef.current !== 'completed') {
          reconnectTimer = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => {};
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [matchId, token, fetchMatch]);

  // Orchestration: drive server transitions (race-safe, any client may fire)
  useEffect(() => {
    if (!match) return;

    const allFilled = (team) => (team?.slots || []).every((s) => s.user_id);
    const allVoted = (team) => {
      const filled = (team?.slots || []).filter((s) => s.user_id && !s.is_abandoned).length;
      return Object.keys(team?.name_votes || {}).length >= filled && filled > 0;
    };

    if (match.state === 'waiting_room' && allFilled(match.team_a) && allFilled(match.team_b) && !readyCalledRef.current) {
      readyCalledRef.current = true;
      fetch(`${API_URL}/api/online/match/${matchId}/ready`, {
        method: 'POST',
        headers: getAuthHeader(),
      }).then(() => fetchMatch()).catch(() => { readyCalledRef.current = false; });
    }

    if (match.state === 'voting' && allVoted(match.team_a) && allVoted(match.team_b) && !finalizeCalledRef.current) {
      finalizeCalledRef.current = true;
      fetch(`${API_URL}/api/online/match/${matchId}/finalize-votes`, {
        method: 'POST',
        headers: getAuthHeader(),
      }).then(() => fetchMatch()).catch(() => {});
    }

    if (match.state === 'countdown') {
      if (!countdownAnnouncedRef.current) {
        countdownAnnouncedRef.current = true;
        playSuccess();
      }
      if (typeof match.countdown_seconds_remaining === 'number') {
        setCountdown((prev) => (prev === null ? match.countdown_seconds_remaining : Math.min(prev, match.countdown_seconds_remaining)));
      }
    }

    if (match.state === 'in_progress' && !startCalledRef.current) {
      startCalledRef.current = true;
      playHomeRun();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match]);

  // Local 1s countdown ticker; call /start at zero
  useEffect(() => {
    if (match?.state !== 'countdown' || countdown === null) return undefined;
    if (countdown <= 0) {
      if (!startCalledRef.current) {
        startCalledRef.current = true;
        fetch(`${API_URL}/api/online/match/${matchId}/start`, {
          method: 'POST',
          headers: getAuthHeader(),
        }).then(() => fetchMatch()).catch(() => { startCalledRef.current = false; });
      }
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, match?.state]);

  // Navigate to the rematch lobby once it starts (only if I accepted)
  useEffect(() => {
    const rem = match?.rematch;
    if (
      match?.state === 'completed' &&
      rem?.status === 'started' &&
      rem.new_match_id &&
      rem.new_match_id !== matchId &&
      (rem.accepted || []).includes(user?.id)
    ) {
      toast.success('Rematch starting!');
      navigate(`/online/lobby/${rem.new_match_id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.rematch, match?.state]);

  const handleVote = async (name) => {
    playClick();
    try {
      const res = await fetch(`${API_URL}/api/online/match/${matchId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ team_name: name }),
      });
      if (!res.ok) throw new Error('vote failed');
      // Notify others live via WS broadcast
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'vote', payload: { team_name: name } }));
      }
      fetchMatch();
    } catch (e) {
      toast.error('Vote failed');
    }
  };

  const handleAbandon = async () => {
    playClick();
    try {
      await fetch(`${API_URL}/api/online/match/${matchId}/abandon`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
    } catch (e) {
      // ignore
    }
    navigate('/dashboard');
  };

  if (!match) {
    return (
      <RetroBackground variant="grass" className="p-4 flex items-center justify-center">
        <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '14px', color: PALETTE.cream }}>
          Loading match...
        </p>
      </RetroBackground>
    );
  }

  const myTeamData = match.my_team === 'A' ? match.team_a : match.team_b;
  const myVote = myTeamData?.name_votes?.[Object.keys(myTeamData?.name_votes || {}).find(
    (uid) => myTeamData.name_votes[uid] && uid === match.my_user_id
  )] || match.my_vote;
  const officialPlaylist = playlists.find((p) => p.id === match.official_playlist_id);

  const stateLabel = {
    waiting_room: 'WAITING ROOM',
    voting: 'TEAM NAME VOTE',
    countdown: 'GET READY!',
    in_progress: 'GAME ON!',
    completed: 'MATCH COMPLETE',
  }[match.state] || 'WAITING ROOM';

  return (
    <RetroBackground variant="grass" className="p-4 md:p-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        {match.state === 'in_progress' ? (
          <p
            className="text-center mb-3"
            style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '11px',
              color: `${PALETTE.cream}cc`,
            }}
            data-testid="in-game-header"
          >
            ♪ {officialPlaylist?.name || 'Official Playlist'} • {wsConnected ? 'LIVE' : 'RECONNECTING...'}
          </p>
        ) : (
          <div className="text-center mb-6">
            <RetroPageTitle color="cream" size="md">
              {stateLabel}
            </RetroPageTitle>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '12px',
              color: `${PALETTE.cream}cc`,
              marginTop: '6px',
            }}
              data-testid="lobby-connection-status"
            >
              {match.team_size}v{match.team_size} • {match.total_innings} innings
              {match.state === 'completed' ? ' • FINAL' : ` • ${wsConnected ? 'LIVE' : 'RECONNECTING...'}`}
            </p>
          </div>
        )}

        {/* Countdown LED display */}
        {match.state === 'countdown' && (
          <div
            className="text-center p-6 mb-6"
            style={{
              background: PALETTE.scoreboardBg,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
            }}
            data-testid="countdown-panel"
          >
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '11px',
              color: '#FFFFFF',
              letterSpacing: '2px',
              marginBottom: '12px',
            }}>
              {(match.team_a?.name || 'TEAM A').toUpperCase()} VS {(match.team_b?.name || 'TEAM B').toUpperCase()}
            </p>
            <p
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '56px',
                lineHeight: 1,
                color: PALETTE.scoreboardText,
                textShadow: `0 0 18px ${PALETTE.scoreboardText}88`,
              }}
              data-testid="countdown-value"
            >
              {countdown ?? match.countdown_seconds_remaining ?? 10}
            </p>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '12px',
              color: `${PALETTE.cream}99`,
              marginTop: '12px',
            }}>
              Game starting...
            </p>
          </div>
        )}

        {/* In-game screens */}
        {match.state === 'in_progress' && (
          <>
            <OnlineGame
              matchId={matchId}
              match={match}
              gameMsg={gameMsg}
              getAuthHeader={getAuthHeader}
              refetchMatch={fetchMatch}
            />
            <div className="mt-4">
              <RetroButton
                variant="danger"
                size="sm"
                fullWidth
                onClick={() => { playClick(); setConfirmAbandon(true); }}
                data-testid="abandon-match-btn"
              >
                ABANDON MATCH
              </RetroButton>
            </div>
          </>
        )}

        {/* Match complete - full endgame sequence */}
        {match.state === 'completed' && (
          <OnlineEndGame
            matchId={matchId}
            getAuthHeader={getAuthHeader}
            match={match}
            refetchMatch={fetchMatch}
          />
        )}

        {/* Teams (pre-game states only) */}
        {match.state !== 'in_progress' && match.state !== 'completed' && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <TeamPanel teamId="A" team={match.team_a} isMyTeam={match.my_team === 'A'} />
            <TeamPanel teamId="B" team={match.team_b} isMyTeam={match.my_team === 'B'} />
          </div>
        )}

        {/* Team name voting */}
        {match.state === 'voting' && match.team_name_options && !myTeamData?.name && (
          <div
            className="mb-6"
            style={{
              background: PALETTE.cream,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.lg,
              boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
              overflow: 'hidden',
            }}
            data-testid="vote-panel"
          >
            <div
              className="text-center py-2"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '11px',
                color: PALETTE.ink,
                background: ACCENTS.gold,
                borderBottom: `3px solid ${PALETTE.ink}`,
                letterSpacing: '1px',
              }}
            >
              VOTE FOR YOUR TEAM NAME
            </div>
            <div className="p-3 space-y-2">
              {match.team_name_options.map((name) => {
                const isMine = match.my_vote === name || myVote === name;
                return (
                  <button
                    key={name}
                    onClick={() => handleVote(name)}
                    className="w-full px-4 py-2 text-center transition-all"
                    style={{
                      border: `3px solid ${PALETTE.ink}`,
                      borderRadius: DESIGN_TOKENS.borderRadius.pill,
                      background: isMine
                        ? bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark)
                        : bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
                      boxShadow: isMine
                        ? `inset 1px 1px 0px #FFF3C4, 2px 2px 0px ${PALETTE.ink}`
                        : `2px 2px 0px ${PALETTE.ink}`,
                    }}
                    data-testid={`vote-option-${name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <span style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontWeight: isMine ? 700 : 600,
                      fontSize: '14px',
                      color: PALETTE.ink,
                    }}>
                      {name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Waiting hint */}
        {match.state === 'waiting_room' && (
          <p
            className="text-center animate-pulse mb-6"
            style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '13px',
              color: PALETTE.cream,
              textShadow: `1px 1px 0px ${PALETTE.ink}`,
            }}
            data-testid="waiting-hint"
          >
            Waiting for all players to join...
          </p>
        )}

        {match.state === 'voting' && myTeamData?.name && (
          <p
            className="text-center animate-pulse mb-6"
            style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '13px',
              color: PALETTE.cream,
              textShadow: `1px 1px 0px ${PALETTE.ink}`,
            }}
          >
            Waiting for the other team to finish voting...
          </p>
        )}

        {/* Leave */}
        {match.state !== 'in_progress' && match.state !== 'completed' && (
          <RetroButton
            variant="danger"
            size="sm"
            fullWidth
            onClick={() => { playClick(); setConfirmAbandon(true); }}
            data-testid="leave-match-btn"
          >
            LEAVE MATCH
          </RetroButton>
        )}

        {/* Abandon confirmation modal */}
        {confirmAbandon && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            data-testid="abandon-confirm-modal"
          >
            <div
              className="text-center p-5 w-full max-w-sm"
              style={{
                background: PALETTE.cream,
                border: `4px solid ${PALETTE.ink}`,
                borderRadius: DESIGN_TOKENS.borderRadius.lg,
                boxShadow: '6px 6px 0px rgba(0,0,0,0.5)',
              }}
            >
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '13px',
                color: '#E23B2E',
                letterSpacing: '1px',
                marginBottom: '10px',
              }}>
                ABANDON MATCH?
              </p>
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: '13px',
                color: PALETTE.ink,
                marginBottom: '16px',
              }}>
                {match?.state === 'in_progress'
                  ? (match?.team_size === 1
                    ? "You'll forfeit the game and take the loss."
                    : 'Your team will play short-handed without you.')
                  : "You'll leave this match and return to the dashboard."}
              </p>
              <div className="flex gap-2">
                <RetroButton
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => { playClick(); setConfirmAbandon(false); }}
                  data-testid="abandon-cancel-btn"
                >
                  STAY
                </RetroButton>
                <RetroButton
                  variant="danger"
                  size="sm"
                  fullWidth
                  onClick={handleAbandon}
                  data-testid="abandon-confirm-btn"
                >
                  ABANDON
                </RetroButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </RetroBackground>
  );
}
